<script lang="ts" setup>
  import { useToast } from '@/components/ui/toast'
  import { useTranslation } from '@/composables/useTranslation'
  import { FILE_INPUT_ACCEPT, isFileSupported } from '@/constants/documentFileTypes'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import type { CustomerCase } from '@/types/generated'
  import { Upload, CircleCheck, CircleX, Loader2, RotateCw } from 'lucide-vue-next'
  import type { PropType } from 'vue'
  import { computed, defineEmits, defineProps, ref } from 'vue'

  enum UploadStatus {
    PENDING = 'pending',
    UPLOADING = 'uploading',
    SUCCESS = 'success',
    ERROR = 'error'
  }

  interface FileUploadState {
    name: string
    file: File
    status: UploadStatus
    error?: string
  }

  const emit = defineEmits(['fileUploaded', 'filesSelected', 'allUploadsComplete'])

  const props = defineProps({
    caseData: {
      type: Object as PropType<CustomerCase>,
      required: true
    },
    maxSize: {
      type: Number,
      default: 100
    },
    showUploadingList: {
      type: Boolean,
      default: false
    }
  })

  const { toast } = useToast()

  const { t } = useTranslation({
    en: {
      DropFilesHere: 'Drop your files here or',
      Browse: 'browse',
      MaximumSize: 'Maximum size',
      UnsupportedFileType: 'Unsupported file type',
      UploadFailed: 'Upload failed',
      Retry: 'Retry'
    },
    de: {
      DropFilesHere: 'Legen Sie Ihre Datei hier ab oder',
      Browse: 'durchsuchen',
      MaximumSize: 'Maximale Größe',
      UnsupportedFileType: 'Nicht unterstützter Dateityp',
      UploadFailed: 'Upload fehlgeschlagen',
      Retry: 'Erneut versuchen'
    }
  })

  const caseStore = useCaseStore()
  const fileInput = ref<HTMLInputElement | null>(null)
  const dropZone = ref<HTMLDivElement | null>(null)

  // === UPLOAD STATE ===
  const isUploading = ref(false)
  const fileStates = ref<FileUploadState[]>([])

  const hasErrors = computed(() => fileStates.value.some((f) => f.status === UploadStatus.ERROR))
  const hasUploading = computed(() =>
    fileStates.value.some((f) => f.status === UploadStatus.UPLOADING)
  )

  // === EVENT HANDLERS ===

  const onDrop = (e: DragEvent) => {
    e.preventDefault()
    const files = e.dataTransfer?.files
    if (!files) return

    const allFiles = Array.from(files)
    const supported = allFiles.filter((f) => isFileSupported(f))
    const rejected = allFiles.filter((f) => !isFileSupported(f))

    if (rejected.length > 0) {
      toast({
        title: t('UnsupportedFileType'),
        description: rejected.map((f) => f.name).join(', '),
        variant: 'destructive'
      })
    }

    if (supported.length > 0) {
      handleFiles(supported)
    }
  }

  const onFileInput = (e: Event) => {
    const input = e.target as HTMLInputElement
    if (input.files) {
      handleFiles(Array.from(input.files))
    }
  }

  // === FILE PROCESSING ===

  const uploadSingleFile = async (fileState: FileUploadState) => {
    fileState.status = UploadStatus.UPLOADING
    fileState.error = undefined

    try {
      const response = await caseStore.documentProcessor.uploadDocument(fileState.file)
      fileState.status = UploadStatus.SUCCESS

      if (response.uuid) {
        emit('fileUploaded', response.uuid)
      }
    } catch (error: any) {
      const errorMessage = error?.body?.detail || error?.body?.file?.[0] || t('UploadFailed')
      fileState.status = UploadStatus.ERROR
      fileState.error = errorMessage
    }
  }

  const handleFiles = async (files: File[]) => {
    const validFiles = files.filter((file) => {
      const isValidSize = file.size <= props.maxSize * 1024 * 1024
      if (!isValidSize) {
        console.warn(
          `File ${file.name} is too large (${(file.size / (1024 * 1024)).toFixed(2)} MB)`
        )
      }
      return isValidSize
    })

    if (validFiles.length === 0) return

    emit('filesSelected', validFiles)

    isUploading.value = true
    fileStates.value = validFiles.map((f) => ({
      name: f.name,
      file: f,
      status: UploadStatus.UPLOADING
    }))

    await Promise.allSettled(fileStates.value.map((fs) => uploadSingleFile(fs)))

    isUploading.value = false

    if (!hasErrors.value) {
      emit('allUploadsComplete')
    }
  }

  const retryFile = async (fileState: FileUploadState) => {
    await uploadSingleFile(fileState)

    if (!hasErrors.value && !hasUploading.value) {
      emit('allUploadsComplete')
    }
  }

  // === DRAG & DROP HANDLERS ===

  const preventDefaults = (e: Event) => {
    e.preventDefault()
    e.stopPropagation()
  }
</script>

<template>
  <div>
    <!-- Drop zone (hidden while showing results) -->
    <div
      v-if="fileStates.length === 0"
      ref="dropZone"
      class="flex cursor-pointer justify-center rounded-xl border border-dashed border-gray-300 bg-white p-6"
      @dragover.prevent="preventDefaults"
      @dragenter.prevent="preventDefaults"
      @dragleave.prevent="preventDefaults"
      @drop="onDrop"
      @click="fileInput?.click()"
    >
      <input
        ref="fileInput"
        type="file"
        class="hidden"
        multiple
        :accept="FILE_INPUT_ACCEPT"
        @change="onFileInput"
      />
      <div class="text-center">
        <span
          class="inline-flex size-12 items-center justify-center rounded-full bg-gray-100 text-gray-800"
        >
          <Upload class="size-5 shrink-0" />
        </span>

        <div class="mt-2 flex flex-wrap justify-center text-sm leading-6 text-gray-600">
          <span class="pe-1 font-medium text-gray-800">{{ t('DropFilesHere') }}</span>
          <span
            class="rounded-lg bg-white font-semibold text-purple-600 decoration-2 focus-within:outline-none
              focus-within:ring-2 focus-within:ring-purple-600 focus-within:ring-offset-2 hover:text-purple-700
              hover:underline"
          >
            {{ t('Browse') }}
          </span>
        </div>

        <p class="mt-1 text-xs text-gray-400">{{ t('MaximumSize') }} {{ props.maxSize }} MB.</p>
      </div>
    </div>

    <!-- File upload status list -->
    <div
      v-else
      class="space-y-2"
    >
      <div
        v-for="file in fileStates"
        :key="file.name"
        class="flex items-start gap-3 rounded-lg border px-3 py-2"
        :class="{
          'border-gray-200 bg-white':
            file.status === UploadStatus.UPLOADING || file.status === UploadStatus.SUCCESS,
          'border-red-200 bg-red-50': file.status === UploadStatus.ERROR
        }"
      >
        <div class="mt-0.5 shrink-0">
          <Loader2
            v-if="file.status === UploadStatus.UPLOADING"
            class="size-4 animate-spin text-purple-600"
          />
          <CircleCheck
            v-else-if="file.status === UploadStatus.SUCCESS"
            class="size-4 text-green-600"
          />
          <CircleX
            v-else-if="file.status === UploadStatus.ERROR"
            class="size-4 text-red-600"
          />
        </div>
        <div class="min-w-0 flex-1">
          <p
            class="truncate text-sm font-medium"
            :class="{
              'text-gray-700':
                file.status === UploadStatus.UPLOADING || file.status === UploadStatus.SUCCESS,
              'text-red-700': file.status === UploadStatus.ERROR
            }"
          >
            {{ file.name }}
          </p>
          <p
            v-if="file.error"
            class="mt-0.5 text-xs text-red-600"
          >
            {{ file.error }}
          </p>
        </div>
        <button
          v-if="file.status === UploadStatus.ERROR"
          class="mt-0.5 shrink-0 rounded p-1 text-red-600 hover:bg-red-100"
          :title="t('Retry')"
          @click="retryFile(file)"
        >
          <RotateCw class="size-3.5" />
        </button>
      </div>
    </div>
  </div>
</template>
